/*
 * Project: bellcloud
 * File: apps/frontend/src/features/guide/GuideButton.tsx
 * Description: 현재 페이지 가이드를 시작하는 버튼. 기존 tertiary 버튼 스타일과 어울리게 구성.
 */

import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useGuide } from './useGuide';
import type { GuideKey } from './types';

const StyledGuideButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
  padding: ${({ theme }) => `${theme.spacing[2]}px ${theme.spacing[4]}px`};
  border-radius: ${({ theme }) => theme.borderRadius.lg}px;
  border: 1px solid ${({ theme }) => theme.colors.button.stroke.tertiaryBrand.default};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.button.text.tertiaryBrand};
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: ${({ theme }) => theme.lineHeight.normal};
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.button.stroke.tertiaryBrand.hover};
    background-color: ${({ theme }) => theme.colors.surface.interactive.hover};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.border.group.brand}30;
  }
`;

/** 아이콘 전용(원형 고스트) 버튼 — 제목 옆 ? 아이콘용 */
const StyledGuideIconButton = styled.button<{ iconSize: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ iconSize }) => iconSize + 12}px;
  height: ${({ iconSize }) => iconSize + 12}px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.icon.emphasis};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surface.interactive.hover};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.border.group.brand}30;
  }
`;

const HelpIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path
      d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .8-1 1.7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="12" cy="17" r="1" fill="currentColor" />
  </svg>
);

interface GuideButtonProps {
  /** 시작할 페이지 가이드 식별자 */
  guideKey: GuideKey | string;
  /** 버튼 문구 커스터마이즈 (기본: i18n guide:view) */
  label?: string;
  /** 텍스트 없이 ? 아이콘만 표시 */
  iconOnly?: boolean;
  /** 아이콘 픽셀 크기 (iconOnly 일 때 사용, 기본 22) */
  iconSize?: number;
  className?: string;
}

export const GuideButton: React.FC<GuideButtonProps> = ({
  guideKey,
  label,
  iconOnly = false,
  iconSize = 22,
  className,
}) => {
  const { t } = useTranslation('guide');
  const { startGuide } = useGuide();
  const text = label ?? t('view');

  if (iconOnly) {
    return (
      <StyledGuideIconButton
        type="button"
        className={className}
        iconSize={iconSize}
        onClick={() => startGuide(guideKey)}
        aria-label={text}
        title={text}
      >
        <HelpIcon size={iconSize} />
      </StyledGuideIconButton>
    );
  }

  return (
    <StyledGuideButton
      type="button"
      className={className}
      onClick={() => startGuide(guideKey)}
      aria-label={text}
    >
      <HelpIcon />
      <span>{text}</span>
    </StyledGuideButton>
  );
};
